import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useGeolocated } from "react-geolocated";
import * as HyperDX from "@hyperdx/browser";
import { Footer } from "@components";
import { Storage } from "@utils";
import ErrorPage from "@pages/errorPage";
import {
  setLanguage,
  setUserCountry,
  setCoordinates,
  upsertRecentSearches,
  upsertRecentViewedNews,
  setDefaultCountryCoordinates,
} from "@redux/user/slice";
import {
  getTerms,
  getSetting,
  getAbout_US,
  getPrivacyData,
  getHomePageSetting,
  getCancellationPolicy,
} from "@redux/settings/actions";
import { getUser } from "@redux/user/actions";
import { change_Language } from "@redux/settings/slice";
import { getCountrysList } from "@redux/teetime/actions";
import { setUserDetails } from "@redux/authentication/slice";
import { getMenus, getHeaderData } from "@redux/header/actions";
import routes from "./routes";
import { HYPER_DX_API } from "./utils/config";

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation("common");
  const { setting } = useSelector((store) => store.settingsReducer);
  const defaultCountry = setting?.data?.country?.name;
  const { current_Language, homePageSetting } = useSelector(
    (store) => store.settingsReducer
  );
  const { coords, isGeolocationAvailable, isGeolocationEnabled } =
    useGeolocated({
      positionOptions: {
        enableHighAccuracy: false,
      },
      userDecisionTimeout: 5000,
    });
  const { userCountry } = useSelector((store) => store.userReducer);
  const { menus, logo } = useSelector((state) => state.headerReducer);
  const { countryList } = useSelector((store) => store.teetimeReducer);

  const [isServerLoading, setIsServerLoading] = useState(true);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const token = query.get("token");
    if (token) {
      navigate(`/reset-password?token=${token}`);
    }

    const hostname = window.location.hostname;

    const parts = hostname.split(".");
    const service =
      parts.length >= 4 ? parts.slice(0, parts.length - 2).join(".") : hostname;

    let environment = "production";

    if (
      hostname.startsWith("demo-") ||
      hostname.startsWith("test-") ||
      hostname.startsWith("localhost")
    ) {
      environment = "development";
    } else if (hostname.startsWith("staging-")) {
      environment = "staging";
    } else if (hostname.startsWith("bookings.")) {
      environment = "production";
    }

    HyperDX.init({
      apiKey: HYPER_DX_API || "a25ad3dc-b911-47fb-8767-6bffb469ebb8",
      service: service,
      environment: environment,
      maskAllInputs: false,
      maskAllText: false,
      disableIntercom: true,
      disableReplay: false,
    });

    console.log("*** domain-name ", hostname , HYPER_DX_API);
    const script = document.createElement("script");
    script.defer = true;
    script.dataset.domain = hostname;
    script.dataset.api = "https://t.g365.golf/api/event";
    script.src =
      "https://t.g365.golf/js/script.hash.outbound-links.pageview-props.revenue.tagged-events.js";
    document.head.appendChild(script);

    // Initialize plausible global function
    window.plausible =
      window.plausible ||
      function () {
        (window.plausible.q = window.plausible.q || []).push(arguments);
      };
  }, [location]);

  useEffect(() => {
    if (!countryList?.length) {
      dispatch(getCountrysList());
    }
    if (!menus) {
      dispatch(getMenus());
    }
    if (!logo) {
      dispatch(getHeaderData());
    }
    if (!setting) {
      dispatch(getSetting(current_Language));
    }

    setTimeout(() => {
      setIsServerLoading(false);
    }, 2000);

    checkUserAuthentication();
    const userData = localStorage.getItem("C2H_UserData");
    if (userData) {
      try {
        const parsedData = JSON.parse(userData);
        if (parsedData && parsedData.id) {
          dispatch(getUser(parsedData.id));
        }
      } catch (error) {}
    }

    const initialize = async () => {
      let recentSearches = await Storage.get(Storage.RECENT_SEARCHES);
      let recentViewedNews = await Storage.get(Storage.RECENT_VIEWED_NEWS);
      if (recentSearches) {
        let parsedRecentSearches = JSON.parse(recentSearches);
        dispatch(upsertRecentSearches(parsedRecentSearches));
      }
      if (recentViewedNews) {
        let parsedRecentViewedNews = JSON.parse(recentViewedNews);
        dispatch(upsertRecentViewedNews(parsedRecentViewedNews));
      }
    };
    initialize();
  }, []);

  useEffect(() => {
    fetch("https://ipapi.co/json/")
      .then((response) => response.json())
      .then((data) => {
        if (data && data?.country_name) {
          dispatch(
            setUserCountry({
              country: data?.country_name,
            })
          );
        }
      })
      .catch((error) => {});
  }, []);

  useEffect(() => {
    if (
      userCountry?.country === "Germany" ||
      userCountry?.country === "Austria" ||
      userCountry?.country === "Belgium" ||
      userCountry?.country === "Luxembourg" ||
      userCountry?.country === "Switzerland" ||
      userCountry?.country === "Liechtenstein"
    ) {
      handleLanguageChange({
        code: "GB",
        language: "en",
      });
    } else {
      handleLanguageChange({
        code: "GB",
        language: "en",
      });
    }
  }, [userCountry]);

  const handleLanguageChange = (props) => {
    dispatch(setLanguage(props));
    i18n.changeLanguage(props.language);
    Storage.save(Storage.LANGUAGE, props.language);
    dispatch(change_Language(props.language));
  };

  useEffect(() => {
    if (isGeolocationAvailable && isGeolocationEnabled && coords) {
      if (coords.latitude && coords.longitude) {
        const fetchCountryURL = `https://api.opencagedata.com/geocode/v1/json?q=${
          coords.latitude
        }+${coords.longitude}&key=${"1c9a9c1c266d47acb66de1b1b354c6e5"}`;

        axios.get(fetchCountryURL).then((response) => {
          if (response?.data?.results) {
            dispatch(
              setCoordinates({
                lat: coords.latitude,
                lng: coords.longitude,
                country: response?.data?.results[0]?.components?.country,
              })
            );
          } else {
            dispatch(
              setCoordinates({
                lat: coords.latitude,
                lng: coords.longitude,
                country: "",
              })
            );
          }
        });
      }
    }
  }, [isGeolocationAvailable, isGeolocationEnabled, coords]);

  useEffect(() => {
    checkLanguage();
    if (!homePageSetting?.length) {
      dispatch(getHomePageSetting(current_Language));
    }
  }, [current_Language]);

  async function checkLanguage() {
    try {
      let selectLanguage = await Storage.get(Storage.LANGUAGE);
      let new_Language = selectLanguage.replace(/"/g, "");
      i18n.changeLanguage(new_Language);
      if (current_Language === new_Language) {
        dispatch(getTerms(`terms-cond?locale=${new_Language}`));
        dispatch(getPrivacyData(`privacy-policy?locale=${new_Language}`));
        dispatch(
          getCancellationPolicy(`cancellation-policy?locale=${new_Language}`)
        );
        dispatch(getAbout_US(`about-us?locale=${new_Language}`));
      } else {
        dispatch(change_Language(new_Language));
      }
    } catch (error) {
      dispatch(getTerms(`terms-cond?locale=${current_Language}`));
      dispatch(getPrivacyData(`privacy-policy?locale=${current_Language}`));
      dispatch(
        getCancellationPolicy(`cancellation-policy?locale=${current_Language}`)
      );
      dispatch(getAbout_US(`about-us?locale=${current_Language}`));
    }
  }

  useEffect(() => {
    if (defaultCountry) {
      const fetchGeolocation = async () => {
        try {
          const response = await fetch(
            `https://api.opencagedata.com/geocode/v1/json?q=${defaultCountry}&key=1c9a9c1c266d47acb66de1b1b354c6e5`
          );
          const data = await response.json();
          if (data?.results?.length > 0) {
            dispatch(
              setDefaultCountryCoordinates({
                lat: data?.results[0]?.geometry?.lat,
                lng: data?.results[0]?.geometry?.lng,
                country: defaultCountry,
              })
            );
          }
        } catch (error) {
          // console.error("Error fetching geolocation data:", error);
        }
      };

      fetchGeolocation();
    }
  }, [defaultCountry]);

  const checkUserAuthentication = async () => {
    let userDetails = await Storage.get(Storage.AUTHENTICATION);
    if (userDetails) {
      let user = JSON.parse(userDetails);
      dispatch(setUserDetails(user));
    }
  };

  const isUnderMaintenance =
    !homePageSetting?.length &&
    !menus?.data?.length &&
    !logo?.data?.id &&
    !setting?.data?.id &&
    !countryList?.length;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {isUnderMaintenance && !isServerLoading ? (
        <div
          style={{
            width: "100%",
            height: "200px",
            display: "flex",
            margin: "auto",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-around",
            backgroundColor: "white",
          }}
        >
          <div>
            <h1
              style={{
                marginTop: "50px",
                color: "gray",
                fontWeight: "bold",
                fontSize: "20px",
              }}
            >
              Welcome To Clubs To Hire{" "}
            </h1>
          </div>
          <div
            style={{
              marginTop: "50px",
              color: "#FFBC00",
              fontWeight: "bold",
              fontSize: "30px",
            }}
          >
            <h1 className="animate-pulse ">Server Under Maintenance </h1>
          </div>
        </div>
      ) : isServerLoading ? (
        <div className="w-full h-[300px] flex justify-center items-center">
          <svg className="w-16 h-16 bg-transparent border-4 animate-spin rounded-full border-t-[white] border-[#FBA619]" />
        </div>
      ) : (
        <div style={{ width: "100%" }}>
          <Routes>
            {routes.map((item, index) => (
              <Route
                key={"" + index}
                path={item.path}
                exact={item.exact}
                name={item.name}
                element={item.element}
              />
            ))}
            <Route path="*" element={<ErrorPage />} />
          </Routes>
          <Footer />
        </div>
      )}
    </div>
  );
};

export default App;
